/* PJC v10 (qa5) — chargement différé des petites images de la fiche (vignettes de la galerie, pastilles-photo des variantes).
   Le `loading="lazy"` natif charge tout ce qui se trouve dans environ 1 à 2 écrans de la zone visible, y compris les 16 vignettes d'une rangée
   défilante et les 21 pastilles d'une liste de couleurs : 37 requêtes qui disputent la bande passante à l'image principale (LCP).
   Ici, les images portent `data-pjc-src` (et éventuellement `data-pjc-srcset`) au lieu de `src` :
   - elles ne sont demandées qu'APRÈS l'image principale de la fiche (chargée ou erreur, délai maximal de sécurité) ;
   - et seulement si elles sont réellement dans (ou à 60 px verticalement de) la zone visible ;
   - une rangée de vignettes charge ses voisines dès qu'on la touche, la fait défiler ou y place le focus ;
   - les diapositives de la galerie (`data-pjc-root="slideshow-slides"`) sont observées dans leur rail horizontal avec 50 % de sa largeur d'avance : la voisine
     (puis la suivante quand on avance) est prête avant le balayage, mais pas les 20 autres.
   Le morphing du sélecteur de variantes (assets/morph.js) réécrit les attributs d'après le rendu serveur (sans `src`) : l'observateur ci-dessous
   remet aussitôt le `src` déjà chargé (avant la peinture, depuis le cache mémoire), donc pas de pastille vide après un changement d'option. */

const SELECTOR = 'img[data-pjc-src]';
const VERTICAL_MARGIN = '60px 0px';
const RAIL_MARGIN = '0px 50%';
const MAX_WAIT_MS = 3000;

/** @type {Set<HTMLImageElement>} */
const inView = new Set();
/** @type {WeakMap<HTMLImageElement, { src: string, srcset: string }>} */
const loaded = new WeakMap();
/** @type {WeakMap<HTMLImageElement, IntersectionObserver>} */
const owner = new WeakMap();
/** @type {WeakMap<Element, IntersectionObserver>} */
const railObservers = new WeakMap();
let released = false;

/** @param {HTMLImageElement} img */
function load(img) {
  inView.delete(img);
  owner.get(img)?.unobserve(img);
  owner.delete(img);
  const { pjcSrc, pjcSrcset } = img.dataset;
  if (pjcSrcset && img.getAttribute('srcset') !== pjcSrcset) img.srcset = pjcSrcset;
  if (pjcSrc && img.getAttribute('src') !== pjcSrc) img.src = pjcSrc;
  loaded.set(img, { src: pjcSrc ?? '', srcset: pjcSrcset ?? '' });
}

/** Remet le `src` / `srcset` déjà chargés si un morphing les a retirés. @param {HTMLImageElement} img */
function restore(img) {
  const saved = loaded.get(img);
  if (!saved) return;
  if (img.dataset.pjcSrc !== saved.src) {
    loaded.delete(img);
    return;
  }
  if (img.getAttribute('src') !== saved.src) img.setAttribute('src', saved.src);
  if (saved.srcset && img.getAttribute('srcset') !== saved.srcset) img.setAttribute('srcset', saved.srcset);
}

/** @param {IntersectionObserverEntry[]} entries */
function onIntersect(entries) {
  for (const entry of entries) {
    const img = /** @type {HTMLImageElement} */ (entry.target);
    if (!entry.isIntersecting) {
      inView.delete(img);
    } else if (released) {
      load(img);
    } else {
      inView.add(img);
    }
  }
}

const supported = 'IntersectionObserver' in window;
const observer = supported ? new IntersectionObserver(onIntersect, { rootMargin: VERTICAL_MARGIN }) : null;

/** Observateur d'une image : celui de la fenêtre, ou celui de son rail horizontal (`data-pjc-root`). @param {HTMLImageElement} img */
function observerFor(img) {
  const selector = img.dataset.pjcRoot;
  const root = selector ? img.closest(selector) : null;
  if (!root) return observer;
  let railObserver = railObservers.get(root);
  if (!railObserver) {
    railObserver = new IntersectionObserver(onIntersect, { root, rootMargin: RAIL_MARGIN });
    railObservers.set(root, railObserver);
  }
  return railObserver;
}

/** @param {Element} scope */
function observe(scope) {
  const list = scope.matches?.(SELECTOR) ? [scope] : [];
  list.push(...scope.querySelectorAll?.(SELECTOR) ?? []);
  for (const img of list) {
    if (!(img instanceof HTMLImageElement) || img.getAttribute('src')) continue;
    const target = observerFor(img);
    if (target) {
      if (owner.get(img) !== target) owner.get(img)?.unobserve(img);
      owner.set(img, target);
      target.observe(img);
    } else {
      load(img);
    }
  }
}

function release() {
  if (released) return;
  released = true;
  for (const img of [...inView]) load(img);
}

/** Charge toutes les images différées d'une rangée de vignettes (le visiteur la manipule). */
function loadRail(event) {
  const rail = event.target instanceof Element ? event.target.closest('.pjc-gallery__thumbs') : null;
  if (!rail) return;
  rail.querySelectorAll(SELECTOR).forEach((img) => img instanceof HTMLImageElement && load(img));
}

// Image principale : la 1re photo de la galerie (priorité haute). Sans elle (accueil, collection), rien à attendre.
const hero = document.querySelector('.product-media__image[fetchpriority="high"]');
if (hero instanceof HTMLImageElement && !hero.complete) {
  hero.addEventListener('load', release, { once: true });
  hero.addEventListener('error', release, { once: true });
  window.setTimeout(release, MAX_WAIT_MS);
} else {
  released = true;
}

observe(document.documentElement);

// Images ajoutées plus tard (morphing de section, fenêtre d'ajout rapide, rechargement de section de l'éditeur).
new MutationObserver((records) => {
  for (const record of records) {
    if (record.type === 'attributes') {
      if (record.target instanceof HTMLImageElement) {
        restore(record.target);
        observe(record.target);
      }
    } else {
      record.addedNodes.forEach((node) => node instanceof Element && observe(node));
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-pjc-src', 'src', 'srcset'] });

document.addEventListener('scroll', loadRail, { capture: true, passive: true });
document.addEventListener('pointerdown', loadRail, { capture: true, passive: true });
document.addEventListener('focusin', loadRail);
